<template>
  <el-form ref="form" :model="form" :rules="rules" :disabled="disabled || submitting"
           label-position="top" class="violation-form" @submit.native.prevent="submitForm">
    <h4>
      <svg class="section-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <circle cx="12" cy="8" r="4" />
        <path d="M5 21v-2a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v2" />
      </svg>
      <span>1. Thông tin học viên</span>
    </h4>
    <el-form-item label="Học viên" prop="user_id">
      <el-select v-model="form.user_id" filterable clearable class="student-select"
                 placeholder="Nhập họ tên, số điện thoại, mã học viên" no-data-text="Chưa có học viên trong ca thi"
                 no-match-text="Không tìm thấy học viên" @change="changeStudent">
        <el-option v-for="student in students" :key="student.user_id"
                   :value="student.user_id" :label="studentLabel(student)" />
      </el-select>
    </el-form-item>
    <h4>
      <svg class="section-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="m14 3 7 7-4 4-7-7zM8 9l7 7M12 12l-8 8M3 21h10" />
      </svg>
      <span>2. Thông tin vi phạm</span>
    </h4>
    <el-form-item label="Loại vi phạm" prop="violation_type">
      <el-radio-group v-model="form.violation_type" class="type-options" @change="changeType">
        <el-radio v-for="type in types" :key="type.id" :label="type.id">{{ type.name }}</el-radio>
      </el-radio-group>
    </el-form-item>
    <el-form-item v-if="form.violation_type === REGULATION" label="Kĩ năng vi phạm" prop="skill_ids">
      <el-checkbox-group v-model="form.skill_ids" class="skill-options" @change="validateSkills">
        <el-checkbox v-for="skill in skills" :key="skill.id" :label="skill.id">{{ skill.name }}</el-checkbox>
      </el-checkbox-group>
    </el-form-item>
    <p v-if="form.violation_type" class="violation-warning" role="status">
      <i class="el-icon-warning-outline" />
      <template v-if="form.violation_type === FRAUD">Lưu ý: Khi học viên bị đánh dấu gian lận, TẤT CẢ các kỹ năng trong kỳ thi này sẽ được tính là 0 điểm.</template>
      <template v-else>Lưu ý: Khi học viên bị đánh dấu vi phạm quy chế, kỹ năng vi phạm sẽ được tính là 0 điểm.</template>
    </p>
    <el-form-item label="Hành vi vi phạm" prop="reason_ids" class="behavior-field">
      <el-checkbox-group v-model="form.reason_ids" class="behavior-options" :disabled="loadingReasons || !!reasonError" @change="clearReasonValidation">
        <el-checkbox v-for="reason in reasons" :key="reason.id" :label="reason.id">{{ reason.reason }}</el-checkbox>
      </el-checkbox-group>
      <span v-if="loadingReasons" class="field-hint"><i class="el-icon-loading" /> Đang tải hành vi vi phạm...</span>
      <span v-else-if="reasonError" class="reason-error" role="alert">{{ reasonError }} <el-button type="text" @click="$emit('retry-reasons')">Thử lại</el-button></span>
      <span v-else-if="!reasons.length" class="field-hint">{{ form.violation_type ? 'Chưa có hành vi vi phạm.' : 'Chọn loại vi phạm để hiển thị hành vi.' }}</span>
    </el-form-item>
    <el-form-item label="Chi tiết hành vi" prop="description">
      <el-input v-model="form.description" type="textarea" :autosize="{ minRows: 1, maxRows: 5 }" placeholder="Nhập mô tả chi tiết" />
    </el-form-item>
    <h4>
      <svg class="section-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M14 3H5v18h14V8zM14 3v5h5M8 14h8M12 10v8" />
      </svg>
      <span>3. Bằng chứng</span>
    </h4>
    <el-form-item label="Link folder biên bản & bằng chứng vi phạm" prop="evidence">
      <el-input v-model="form.evidence" placeholder="Nhập thông tin bằng chứng" />
      <a v-if="safeLink(form.evidence)" :href="safeLink(form.evidence)" target="_blank" rel="noopener noreferrer">Mở bằng chứng <i class="el-icon-top-right" /></a>
    </el-form-item>
    <h4>
      <svg class="section-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M14 3H5v18h14V8zM14 3v5h5M8 12h8M8 16h8" />
      </svg>
      <span>4. Xử lý tại thời điểm thi</span>
    </h4>
    <el-form-item label="Record xử lý / trao đổi" prop="handling_record">
      <el-input v-model="form.handling_record" type="textarea" :autosize="{ minRows: 1, maxRows: 5 }" placeholder="Nhập thông tin trao đổi với học viên" />
      <a v-if="safeLink(form.handling_record)" :href="safeLink(form.handling_record)" target="_blank" rel="noopener noreferrer">Mở record xử lý / trao đổi <i class="el-icon-top-right" /></a>
    </el-form-item>
    <div class="violation-actions">
      <el-button size="small" :disabled="submitting" @click="$emit('cancel')">Hủy</el-button>
      <el-button size="small" type="primary" native-type="submit" :loading="submitting"
                 :disabled="disabled || submitting || loadingReasons || !!reasonError">Ghi nhận vi phạm</el-button>
    </div>
  </el-form>
</template>

<script>
import { FRAUD, REGULATION, VIOLATION_TYPES, VIOLATION_SKILLS, violationStudentLabel, violationLink } from '@/core/option/examViolation';

const emptyForm = () => ({ user_id: null, violation_type: '', skill_ids: [], reason_ids: [], description: '', evidence: '', handling_record: '' });
export default {
  name: 'ViolationForm',
  props: {
    students: { type: Array, default: () => [] },
    reasons: { type: Array, default: () => [] },
    loadingReasons: { type: Boolean, default: false },
    reasonError: { type: String, default: '' },
    submitting: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false }
  },
  data() {
    return {
      FRAUD, REGULATION, types: VIOLATION_TYPES, skills: VIOLATION_SKILLS,
      form: emptyForm(),
      rules: {
        user_id: [{ required: true, message: 'Vui lòng chọn học viên.', trigger: 'change' }],
        violation_type: [{ required: true, message: 'Vui lòng chọn loại vi phạm.', trigger: 'change' }],
        skill_ids: [{ type: 'array', required: true, min: 1, message: 'Vui lòng chọn ít nhất một kỹ năng.', trigger: 'blur' }],
        reason_ids: [{ type: 'array', required: true, min: 1, message: 'Vui lòng chọn ít nhất một hành vi.', trigger: 'blur' }],
        evidence: [{ required: true, whitespace: true, message: 'Vui lòng nhập thông tin bằng chứng.', trigger: 'blur' }],
        handling_record: [{ required: true, whitespace: true, message: 'Vui lòng nhập thông tin trao đổi với học viên.', trigger: 'blur' }]
      }
    };
  },
  computed: {
    selectedStudent() {
      return this.students.find(student => student.user_id === this.form.user_id);
    }
  },
  methods: {
    studentLabel: violationStudentLabel,
    safeLink: violationLink,
    validateSkills() {
      // Validate user selections, not the programmatic reset when switching violation types.
      this.$refs.form.validateField('skill_ids');
    },
    clearReasonValidation() {
      this.$refs.form.clearValidate('reason_ids');
    },
    changeStudent() {
      this.form.reason_ids = [];
      this.$emit('reason-query', {
        violation_type: this.form.violation_type,
        exam_form: this.selectedStudent ? this.selectedStudent.exam_form : null
      });
      this.$nextTick(() => this.$refs.form.clearValidate('reason_ids'));
    },
    changeType() {
      this.form.skill_ids = [];
      this.changeStudent();
    },
    resetForm() {
      this.form = emptyForm();
      this.$emit('reason-query', null);
      this.$nextTick(() => this.$refs.form.clearValidate());
    },
    submitForm() {
      if (this.disabled || this.submitting || this.loadingReasons || this.reasonError) return;
      this.$refs.form.validate(valid => {
        if (valid) this.$emit('submit', { ...this.form, skill_ids: [...this.form.skill_ids], reason_ids: [...this.form.reason_ids] });
        else this.$nextTick(() => {
          const invalid = this.$el.querySelector('.is-error');
          if (invalid) invalid.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
      });
    }
  }
};
</script>

<style scoped lang="scss" src="./ViolationForm.scss"></style>
